import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../Icon';
import WellbeingWheel from './WellbeingWheel';
import ChallengePanel from './ChallengePanel';
import { ghostBtn, primaryBtn } from './ui';
import api from '../../lib/api';
import { logError } from '../../lib/log';
import { RECOMMENDATIONS, SPOKES } from '../../lib/assessment';

// Sections 1-6 of My Wellbeing Space. "Today's action" is a view on the
// existing habits + streak feature, never a second tracker.
export const WellbeingSpaceTop = ({ name, habits, progressByHabit, logHabit, streak, onHabitsChanged }) => {
  const [assessment, setAssessment] = useState(null);
  const [checkinDue, setCheckinDue] = useState(false);
  const [checkins, setCheckins] = useState([]);
  const [challenges, setChallenges] = useState({ active: null, history: [] });
  const [reading, setReading] = useState({ slugs: [], read: 0, total: 0, last_slug: null });
  const [guides, setGuides] = useState([]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState([]);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    try {
      const [a, c, ch, r, g] = await Promise.all([
        api.get('/me/wellbeing/assessment'),
        api.get('/me/wellbeing/checkins'),
        api.get('/me/challenges'),
        api.get('/me/reading'),
        api.get('/knowledge/published-guides'),
      ]);
      setAssessment(a.data.assessment);
      setCheckinDue(!!a.data.checkin_due);
      setCheckins(c.data || []);
      setChallenges(ch.data || { active: null, history: [] });
      setReading(r.data);
      setGuides(g.data.items || []);
    } catch (e) {
      logError('WellbeingSpaceTop.load', e);
      setNotice('Something went wrong. Please try again.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const priorities = useMemo(() => assessment?.priorities || [], [assessment]);
  const titles = useMemo(
    () => guides.reduce((acc, t) => ({ ...acc, [t.slug]: t.display_title || t.title }), {}),
    [guides],
  );
  const starter = priorities.length ? RECOMMENDATIONS[priorities[0]]?.starter_habit : null;
  const starterHabit = starter ? (habits || []).find((h) => h.title === starter) : null;
  const starterDone = starterHabit ? !!progressByHabit?.[starterHabit.id] : false;
  const recommendedChallenge = priorities
    .map((p) => RECOMMENDATIONS[p]?.challenge)
    .find((id) => !!id) || 'sleep-reset';

  const priorityGuides = useMemo(() => {
    const slugs = [];
    priorities.forEach((p) => (RECOMMENDATIONS[p]?.guides || []).forEach((s) => {
      if (!slugs.includes(s)) slugs.push(s);
    }));
    return slugs;
  }, [priorities]);
  const unread = priorityGuides.filter((s) => !reading.slugs.includes(s));

  const savePriorities = async () => {
    setBusy(true);
    try {
      const { data } = await api.put('/me/wellbeing/priorities', { priorities: draft });
      setAssessment(data.assessment);
      setEditing(false);
    } catch (e) {
      logError('WellbeingSpaceTop.savePriorities', e);
      setNotice('Something went wrong. Please try again.');
    } finally { setBusy(false); }
  };

  const deleteAnswers = async () => {
    if (!window.confirm('Delete your assessment answers and check-ins? This cannot be undone.')) return;
    setBusy(true);
    try {
      await api.delete('/me/wellbeing/assessment');
      setAssessment(null);
      setCheckins([]);
    } catch (e) {
      logError('WellbeingSpaceTop.deleteAnswers', e);
      setNotice('Something went wrong. Please try again.');
    } finally { setBusy(false); }
  };

  const saveCheckin = async (scores) => {
    setBusy(true);
    try {
      await api.post('/me/wellbeing/checkin', { scores });
      await load();
    } catch (e) {
      logError('WellbeingSpaceTop.checkin', e);
      setNotice('Something went wrong. Please try again.');
    } finally { setBusy(false); }
  };

  const joinChallenge = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/me/challenges/${id}/join`);
      setChallenges((c) => ({ ...c, active: data }));
    } catch (e) {
      logError('WellbeingSpaceTop.joinChallenge', e);
      setNotice('Something went wrong. Please try again.');
    } finally { setBusy(false); }
  };

  const completeDay = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/me/challenges/${id}/day`);
      setChallenges((c) => ({ ...c, active: data.status === 'active' ? data : data }));
    } catch (e) {
      logError('WellbeingSpaceTop.completeDay', e);
      setNotice(e.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally { setBusy(false); }
  };

  const addStarterHabit = async () => {
    setBusy(true);
    try {
      await api.post('/me/habits', { title: starter, source_topic_id: null });
      if (onHabitsChanged) await onHabitsChanged();
    } catch (e) {
      logError('WellbeingSpaceTop.addStarter', e);
      setNotice('Something went wrong. Please try again.');
    } finally { setBusy(false); }
  };

  const latestCheckin = checkins.length ? checkins[0].scores : null;

  return (
    <section className="w-full px-margin-mobile lg:px-margin pb-space-2xl" data-testid="wellbeing-space-top">
      <div className="max-w-7xl mx-auto flex flex-col gap-space-lg">
        {notice ? <p className="font-body-md text-body-md text-on-surface-variant" data-testid="space-notice">{notice}</p> : null}

        {/* 1. Welcome and next step */}
        {starter ? (
          <p className="font-body-lg text-body-lg text-on-surface-variant" data-testid="space-next-step">
            <span className="font-label-md text-label-md uppercase tracking-wider text-surface-tint font-bold">Your next step: </span>
            {starter}
          </p>
        ) : null}

        {!assessment ? (
          <div className="bg-surface p-space-lg lg:p-space-xl rounded-lg shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-space-md" data-testid="space-no-assessment">
            <div>
              <h2 className="font-headline-sm text-headline-sm uppercase text-outline mb-space-xs">Start your wellbeing</h2>
              <p className="font-body-md text-body-md text-on-surface-variant">A few simple questions show you where to start. It isn’t a medical assessment.</p>
            </div>
            <Link to="/start" className={primaryBtn} data-testid="space-start-assessment">
              <Icon name="play_arrow" className="text-[18px]" /> <span>Start your wellbeing</span>
            </Link>
          </div>
        ) : (
          <>
            {/* 2. My Wellbeing Wheel */}
            <WellbeingWheel
              wheel={assessment.wheel}
              latest={latestCheckin}
              checkins={checkins}
              checkinDue={checkinDue}
              onCheckin={saveCheckin}
              busy={busy}
            />

            {/* 3. My top 3 priorities */}
            <div className="bg-surface p-space-lg lg:p-space-xl rounded-lg shadow-sm" data-testid="space-priorities">
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-space-sm mb-space-md">
                <div>
                  <span className="font-label-md text-label-md uppercase tracking-widest text-surface-tint font-bold block">WHERE TO FOCUS</span>
                  <h2 className="font-headline-sm text-headline-sm uppercase text-outline">My top 3 priorities</h2>
                </div>
                <div className="flex flex-wrap gap-space-sm">
                  <button type="button" onClick={() => { setDraft(priorities); setEditing(!editing); }} className={ghostBtn} data-testid="space-edit-priorities">
                    <Icon name="edit" className="text-[18px]" /> <span>Change my priorities</span>
                  </button>
                  <Link to="/start" className={ghostBtn} data-testid="space-retake">
                    <Icon name="refresh" className="text-[18px]" /> <span>Retake</span>
                  </Link>
                </div>
              </div>

              {editing ? (
                <div data-testid="space-priority-editor">
                  <div className="flex flex-wrap gap-space-xs mb-space-md">
                    {SPOKES.map((spoke) => {
                      const on = draft.includes(spoke);
                      return (
                        <button
                          key={spoke}
                          type="button"
                          data-testid={`priority-option-${spoke.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                          onClick={() => setDraft(on ? draft.filter((d) => d !== spoke) : (draft.length < 3 ? [...draft, spoke] : draft))}
                          className={`px-space-md h-10 rounded-lg font-label-md text-label-md uppercase tracking-wider transition-colors ${on ? 'bg-surface-tint text-on-primary' : 'bg-surface-container-low text-outline hover:bg-surface-container'}`}
                        >
                          {spoke}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex gap-space-sm">
                    <button type="button" onClick={savePriorities} disabled={busy || !draft.length} className={primaryBtn} data-testid="space-save-priorities">
                      <Icon name="check" className="text-[18px]" /> <span>Save</span>
                    </button>
                    <button type="button" onClick={() => setEditing(false)} className={ghostBtn}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-space-md">
                  {priorities.map((area, i) => (
                    <div key={area} data-testid={`space-priority-${i + 1}`}>
                      <div className="flex items-center gap-space-sm mb-space-xs">
                        <span className="w-7 h-7 rounded-full bg-surface-tint text-on-primary flex items-center justify-center font-label-sm text-label-sm font-bold">{i + 1}</span>
                        <span className="font-title-md text-title-md text-outline font-bold uppercase tracking-wide">{area}</span>
                      </div>
                      <div className="flex flex-wrap gap-space-xs pl-[38px]">
                        {(RECOMMENDATIONS[area]?.guides || []).map((slug) => (
                          <Link key={slug} to={`/topics/${slug}`} className="px-space-md h-9 rounded-lg bg-surface-container-low text-outline hover:bg-primary-container hover:text-surface-tint transition-colors font-label-sm text-label-sm uppercase tracking-wider inline-flex items-center gap-1">
                            <span>{titles[slug] || slug}</span>
                            {reading.slugs.includes(slug) ? <span className="text-surface-tint font-bold">✓ Read</span> : null}
                          </Link>
                        ))}
                        {RECOMMENDATIONS[area]?.area_page ? (
                          <Link to={RECOMMENDATIONS[area].area_page} className="px-space-md h-9 rounded-lg bg-surface-container-low text-outline hover:bg-primary-container hover:text-surface-tint transition-colors font-label-sm text-label-sm uppercase tracking-wider inline-flex items-center">
                            Explore this area of life
                          </Link>
                        ) : null}
                      </div>
                    </div>
                  ))}
                  <button type="button" onClick={deleteAnswers} disabled={busy} className="self-start font-label-sm text-label-sm uppercase tracking-wider text-on-surface-variant hover:text-error transition-colors" data-testid="space-delete-answers">
                    Delete my answers
                  </button>
                </div>
              )}
            </div>

            {/* 4. Today's action — the existing habit + streak, nothing new stored */}
            {starter ? (
              <div className="bg-surface p-space-lg lg:p-space-xl rounded-lg shadow-sm" data-testid="space-today-action">
                <span className="font-label-md text-label-md uppercase tracking-widest text-surface-tint font-bold block">TODAY</span>
                <h2 className="font-headline-sm text-headline-sm uppercase text-outline mb-space-md">Today’s action</h2>
                <p className="font-body-md text-body-md text-on-surface-variant leading-relaxed mb-space-md">{starter}</p>
                <div className="flex flex-wrap items-center gap-space-md">
                  {starterHabit ? (
                    <button
                      type="button"
                      onClick={() => logHabit(starterHabit.id)}
                      disabled={starterDone}
                      className={primaryBtn}
                      data-testid="space-action-done"
                    >
                      <span>Done today ✓</span>
                    </button>
                  ) : (
                    <button type="button" onClick={addStarterHabit} disabled={busy} className={ghostBtn} data-testid="space-action-add">
                      <Icon name="add_circle" className="text-[18px]" /> <span>Add to my habits</span>
                    </button>
                  )}
                  {streak?.current_streak ? (
                    <span className="font-label-md text-label-md uppercase tracking-wider text-on-surface-variant" data-testid="space-action-streak">
                      {streak.current_streak} {streak.current_streak === 1 ? 'day' : 'days'} in a row
                    </span>
                  ) : null}
                </div>
              </div>
            ) : null}

            {/* 5. My challenge */}
            <ChallengePanel
              active={challenges.active}
              recommendedId={recommendedChallenge}
              onJoin={joinChallenge}
              onDone={completeDay}
              busy={busy}
              isAuthed
            />
          </>
        )}

        {/* 6. My reading */}
        <div className="bg-surface p-space-lg lg:p-space-xl rounded-lg shadow-sm" data-testid="space-reading">
          <span className="font-label-md text-label-md uppercase tracking-widest text-surface-tint font-bold block">MY READING</span>
          <h2 className="font-headline-sm text-headline-sm uppercase text-outline mb-space-md">
            <span data-testid="space-reading-count">You’ve read {reading.read} of {reading.total} guides</span>
          </h2>
          {unread.length ? (
            <>
              <Link to={`/topics/${unread[0]}`} className={primaryBtn} data-testid="space-continue-reading">
                <Icon name="menu_book" className="text-[18px]" /> <span>Continue reading</span>
              </Link>
              <div className="mt-space-md flex flex-wrap gap-space-xs" data-testid="space-unread-guides">
                {unread.map((slug) => (
                  <Link key={slug} to={`/topics/${slug}`} className="px-space-md h-9 rounded-lg bg-surface-container-low text-outline hover:bg-primary-container hover:text-surface-tint transition-colors font-label-sm text-label-sm uppercase tracking-wider inline-flex items-center">
                    {titles[slug] || slug}
                  </Link>
                ))}
              </div>
            </>
          ) : (
            <Link to="/resources" className={ghostBtn} data-testid="space-browse-library">
              <Icon name="menu_book" className="text-[18px]" /> <span>Browse the Library</span>
            </Link>
          )}
        </div>
      </div>
    </section>
  );
};

export default WellbeingSpaceTop;
